import { query } from "@/api/query"
import { $rqStorageTemplatesApiClient } from "@/api/storage"
import { getXOrganizationHeaders } from "@/api/utils"
import { queryClient } from "@/lib/services/instances"
import { isUUIDFormat } from "@/lib/utils/validation"

/**
 * `templates/$id` serves two different kinds of template: a user's own custom
 * one (a UUID, held in the storage service) and a legacy Arduino one (static,
 * non-deletable content). The `$id` route picks one in its `beforeLoad` and
 * puts it in context, so every descendant shares the same definition.
 *
 * The two arms are kept as a discriminated union rather than one "either"
 * query because `ensureValidQueryData` cannot infer a polymorphic (ternary)
 * query and silently breaks its return type — callers must narrow first and
 * pass a concrete one.
 */
export function templateQuery(space: string, id: string) {
  return isUUIDFormat(id)
    ? ({
        isUUID: true,
        options: $rqStorageTemplatesApiClient.queryOptions("get", "/storage/template/v1/{template_id}", {
          params: { header: getXOrganizationHeaders(space), path: { template_id: id } }
        })
      } as const)
    : ({ isUUID: false, options: query["iot-templates"].single(id) } as const)
}

export type TemplateQuery = ReturnType<typeof templateQuery>

/**
 * The template's display name, read from the cache for a descendant's `head`.
 * `head` runs at render, by which point the `$id` route has validated the
 * template and populated the cache, so this never fetches. The two arms must
 * stay separate: narrowing `options` is what types the cached data.
 */
export function templateName(template: TemplateQuery) {
  return template.isUUID
    ? queryClient.getQueryData(template.options.queryKey)?.name
    : queryClient.getQueryData(template.options.queryKey)?.title
}
